import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
  ComponentRef,
} from '@angular/core';
import { WizMathUtils, WizDateUtils } from '@wizeoo/akm-core-lib';
import { WizComponentManager, WizRoot, WizVgroup, WizEventManager, WizEvent, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { CalendarOptions, Calendar, ToolbarInput } from '@fullcalendar/core'; // l'import calendar est nécessaire, sinon ça plante
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import allLocales from '@fullcalendar/core/locales-all';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { WidgetConfigurationItemObj, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { isEqual } from 'lodash-es';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { BaselineEngine, FeiKeyDef, FeiVO, FIELD_KIND, LoopResult, WidgetCell, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';


import { ALK_EVENT_TYPE, TIMELINE_MODE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { Color, colorSets } from '../../ngx-charts/utils/color-sets';
import { AlkEvent } from '../../../framework/events/alk.event';
import { WidgetBasic } from '../base/widget-basic';
import { IWidget, IWidgetFilter } from '../../../manager/widgets/i-widget';
import { ViewData } from '../../../model/view.data';

import { PivotFilterExtendedRenderer } from '../pivot/renderers/filters/pivot-filter-extended.renderer';
import { TimelineEventRenderer } from './timeline-event.renderer';
import { TimelineFieldRenderer } from './filters/timeline-field.renderer';
import { TimelineSettingsPop } from './pops/timeline-settings.pop';
import { StyleVO } from '../../../vo/workingdata/style.vo';
import { styleTrio } from '../../../manager/workingdata/style.engine';
import { E } from '@fullcalendar/resource/internal-common';


/** une clé de contexte pour une entrée de frise */
export interface timelineEntry { 
  startField: string; // le string du filtre de base
  endField: string; // la collection de filtres complémentaires
  startFkd?: FeiKeyDef;
  endFkd?: FeiKeyDef; 
  textColor: string;
  backgroundColor: string;
  label: string;
  kindStart?: string; // date ou workcom (date si pas défini)
  settingsStart?: string; // configuration en cas de workcom
  kindEnd?: string; // date ou workcom (date si pas défini)
  settingsEnd?: string; // configuration en cas de workcom
  style?: string; // style pour rendu conditionnel
  measureStyle?: string; // style par mesure
}


@Component({
  selector: 'timeline-widget',
  templateUrl: './timeline.widget.html',
  styleUrls: ['./timeline.widget.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** affichage des données en mode frise */
export class TimelineWidget extends WidgetBasic implements AfterViewInit, OnDestroy {
  
  constructor( elementRef: ElementRef, private _cdRef: ChangeDetectorRef, protected componentFactoryResolver: ComponentFactoryResolver,
    protected appRef: ApplicationRef, protected injector: Injector
  ) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.TIMELINE;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = TimelineSettingsPop;
  }

  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('vgDetailZone', { read: WizVgroup, static: true }) vgDetailZone: WizVgroup; // zone de filtre, pour écouteur sur setFilter
  @ViewChild('containerFilter', { read: ViewContainerRef, static: true }) containerFilter: ViewContainerRef; // zone d'ajout des champs de filtre
  @ViewChild('containerFields', { read: ViewContainerRef, static: true }) containerFields: ViewContainerRef; // zone d'ajout des champs
  @ViewChild('fullcalendar') fullcalendar: FullCalendarComponent; // calendar

  public filterComponents: ComponentRef<PivotFilterExtendedRenderer>[]; // les champs de filtre
  public fieldsComponents: ComponentRef<TimelineFieldRenderer>[]; // les champs de sélection de dates
  public readyToGo = false; // le calendar est OK, sinon sale effet de refresh

  public tooMany: boolean = false; // trop d'élément pour l'affichage
  public tooManyLabel: string = '';

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public resources = []; // la collection de ressources (les lignes)
  public events = []; // la collection d'events
  public scheme: Color = colorSets[ViewData.DEFAULT_COLOR_SET]; // jeu de couleur

  

  // composants rendus. Pour refresh
  public mountedCompos: ComponentRef<TimelineEventRenderer>[] = [];

  /**
   * injection du contenu Angular sur une entrée html
   * @param info les infos du renderer de base (html)
   */
  private doMount(info) {
    
    const bodyPortalHost = new DomPortalOutlet(
      info.el, // Target the element where you wish to append your component
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );

    const newCompo = new ComponentPortal(TimelineEventRenderer); // renderer à injecter
    const refCompo = bodyPortalHost.attach(newCompo);
    refCompo.instance.data = info.event._def; // passage du data
    // console.log('info', info, refCompo);
    this.mountedCompos.push(refCompo);

  }

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin, resourceTimelinePlugin ],
    editable: false,
    firstDay: 1,
    events: this.events,

    resources: this.resources,
    resourceAreaColumns: [
      {
        field: 'title',
        headerContent: ' '
      },
    ],

    eventDidMount: this.doMount.bind(this), // injection de composant dans les renderers
    fixedMirrorParent: document.body,
    resourceAreaWidth:'200px',
    // windowResize
    initialView: 'meYear',
    // headerToolbar: this.buildToolBar.bind(this),
    // headerToolbar: {
    //   left: 'prev,next,today',
    //   center: 'title',
    //   right: 'meYear,meTrim,meMonth,meHour',
    // },
    datesSet: this.changeDate.bind(this),
    locales: allLocales, 
    locale: 'fr',
    aspectRatio: 2,
    views: {
      meYear: {
        type: 'resourceTimeline',
        duration: { years: 1 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.year.$'),
        slotDuration: { weeks: 1 },
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]
      },
      meTrim: {
        type: 'resourceTimeline',
        duration: { months: 3 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.quarter.$'),
        slotDuration: { weeks: 1 }  ,
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]   
      },
      meMonth: {
        type: 'resourceTimeline',
        duration: { months: 1 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.month.$'),
        slotDuration: { weeks: 1 }  ,
        slotLabelFormat: [
          { week: 'numeric' } // lower level of text
        ]   
      },
      meHour: {
        type: 'resourceTimeline',
        duration: { days: 1 },
        buttonText: WizTranslationManager.getInstance().translateKey('$.common.day.$'),
        slotDuration: { minutes: 15 }  ,
        slotLabelFormat: [
          { hour: 'numeric',
            minute: '2-digit' } // lower level of text
        ]   
      }
    },
    
  };


    /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */

  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    // console.log('timeline drawOrRefresh', origin);
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste
    let mustBuildFilters: boolean = false; // on doit construire les filtres
    let mustFilter: boolean = false; // on doit appliquer les filtres
    let mustSelect: boolean = false; // on doit jouer la sélection de lignes

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // mustBuildFilters = true;
      // mustBuildRows = true;
      // mustSelect = true;
      // mustRefresh = true;
    }
    
    if(origin === WIDGET_ACTION_ORIGIN.READY) { // je me suis dit tout seul que j'étais prêt
      mustBuildFilters = true;
      mustBuildRows = true;
      mustSelect = true;
      mustRefresh = true;
    }

    // fin de calcul d'un FEI requis pour mes calculs
    if(origin === WIDGET_ACTION_ORIGIN.REQUIRED_COMPUTED) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {  
      mustBuildFilters = true;
      mustFilter = true; 
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre... on filtre, et on corrige éventuellement la sélection
    // en cas de champ utilisé en colonne, on reconstruit le grid
    if(origin === WIDGET_ACTION_ORIGIN.FILTERING) {
      mustFilter = true;
      mustRefresh = true;
    }

    // modification du filtre global ... on filtre, et on corrige éventuellement la sélection
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      mustFilter = true;
      mustRefresh = true;
    }

    // modification du scenario global ...
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustSelect = true;
      this.mainDS.refresh(); // on applique le filtre
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let loopResults: Map<FeiVO, LoopResult> = data[0];  
      if(this.fei.scenario === WizConstants.SCEN_KEY_CODE) {
        [...loopResults.keys()].forEach((fei: FeiVO) => {
          if(fei.visionStepLevel === this.fei.visionStepLevel) {
            if(loopResults.get(fei).created.size > 0 || loopResults.get(fei).deleted.size > 0) {
              mustBuildRows = true;
              mustRefresh = true;
            } else {
              mustRefresh = true;
            }
          }
        });
      }
      
      // gestion multiscen... prendre tous les loopers du même niveau
      let loop: LoopResult = loopResults.get(this.fei);
      if(loop?.created.size > 0 || loop?.deleted.size > 0) {
        mustBuildRows = true;
        mustRefresh = true;
      } else {
        mustRefresh = true;
      }
    }
    if(mustBuildFilters) {
      this.buildFilters();
    }

    if(mustFilter) {
      this.model.widgetEngine.refreshFilter(data); // reconstruction du filtre (symboliques)
      this.mainDS.refresh(); // on applique le filtre

      // potentiellement, on déselectionne
      if(this.mainDS.selectedItems?.length > 0) {
        // contrôle de la sélection. On déselection les éléments masqués
        let realSelected: WidgetLine[] = [];
        this.mainDS.selectedItems.forEach((line: WidgetLine) => {
          if(!!this.mainDS.getItemByKey(line.key)) {
            realSelected.push(line);
          }
        });

        if(realSelected.length === this.mainDS.selectedItems.length) { // tout est OK

        } else {
          this.mainDS.select(realSelected);
          this.onSelectionChanged(); // persistence du contexte de sélection
          this.updateComponentSelection(); // informer les esclaves
        }
      }
    }

    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustSelect) {
      this.feedSelectionFromContext(this._context); // alimentation de la sélection et passage à WIDGET_STATE = SELECTION_COMPLETE
    }

    if(mustRefresh) {
      this.mainDS.refresh();
      // console.log('draw, buildcal', origin);
      this.buildCalendar();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  // /* ##############################################################################
  //         gestion du contexte de dates
  // ############################################################################## */

  // /**
  //  * détection du changement de date ou du mode de vue
  //  * pour sauvegarde du contexte
  //  * @param arg 
  //  */
  // public changeDate(arg: any): void {
  //   // console.log('changeDate', arg);
  //   this.context.cp = [];
  //   this.context.cp.push({cod: 'mode', val: arg.view.type});
  //   this.context.cp.push({cod: 'day', val: WizDateUtils.dateToString(arg.start, 'yyyy-MM-dd')});
  //   if (!this.templatingMode) {
  //     // persistence uniquement hors du template, sinon, c'est délégué
  //     this._saveContext();
  //   } else {
  //     this.settings = this.context;
  //     WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
  //   }
  // }

  // public getContextDate(): string {
  //   let result: string = WizDateUtils.dateToString(WizDateUtils.TODAY, 'yyyy-MM-dd') ;
  //   if(!!this.context.cp) {
  //     this.context.cp.forEach(wkp => {
  //       if(wkp.cod === 'day') {
  //         result = wkp.val.toString();
  //       }
  //     });
      
  //   }
  //   return result;
  // }

  // private getContextMode(): string {
  //   let result: string = 'meYear';
  //   if(!!this.context.cp) {
  //     this.context.cp.forEach(wkp => {
  //       if(wkp.cod === 'mode') {
  //         result = wkp.val.toString();
  //       }
  //     });
  //   }
  //   return result;
  // }

  /* ##############################################################################
                    gestion des sélections
     ############################################################################## */

  /**
   * changement de mon pool de sélection... intercepté sur AgGrid
   * on va informer les éventuels esclaves.
   * @param e 
   */
  public innerSelectionChange(e: WizEvent): void {
    if(this.state < WIDGET_STATE.READY) { // pas encore affiché, inutile
      return;
    }
    this.onSelectionChanged(); // persistence du contexte de sélection
    this.updateComponentSelection(); // informer le monde que le master (éventuel) a changé
  }

  /** mémorisation du contexte de sélection */
  public onSelectionChanged(): void {
    if(!this._context) { // inutile si le contexte n'est pas instancié (incexc, vsp)
      return; 
    }
    
    // on fait des paquets de 50 en fonction de l'indice
    // dans ces paquets de 1000, on donne un ordre binaire 2^indice
    // on stocke jusqu'a 50 chiffres, {indice, valeur}
    // exemple, sélection 1, 10, 1001 et 3000
    // 0#1025|10#1|29#1.26765E+30
    const selectMap: Map<number, number> = new Map<number, number>();
    let rankPart: number;
    let binRankPart: number;

    this.mainDS.selectedItems?.forEach((line: WidgetLine) => {
      rankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[0];
      binRankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[1];

      if (!selectMap.has(rankPart)) {
        selectMap.set(rankPart, binRankPart);
      } else {
        selectMap.set(rankPart, selectMap.get(rankPart) + binRankPart);
      }
    });

    const selKey: any[] = []; // alimentation des clés dans le contexte
    selectMap.forEach((val: number, key: number) => {
      selKey.push({ k: key, v: val });
    });

    const context: WidgetConfigurationObj = this._context;
    
    // d'abord on vérifie que ça a changé
    let hasUpdate: boolean = !isEqual(context.sel, selKey);
    context.sel = selKey;

    this.updateContext(context, hasUpdate);
  }

  /**
   * alimentation initiale de la sélection
   * @param context 
   */
  public feedSelectionFromContext(context: WidgetConfigurationObj): void {
    const selectPool: WidgetLine[] = [];
    
    if (context.sel?.length > 0) { // pas de context alimenté sur incExc

      // TODO contrôle de cohérence des sélections obsolètes ?
      const selectionKeyMap: Map<number, number> = new Map(context.sel.map((obj) => [+obj.k, +obj.v]));

      let rankPart: number;
      let binRankPart: number;
      this.mainDS.getWorkedSource().forEach((line: WidgetLine) => {
        rankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[0];
        binRankPart = +line.binKey.split(ViewData.CONTEXT_SEPARATOR)[1];
        if (selectionKeyMap.has(rankPart) && WizMathUtils.wizBitwize(selectionKeyMap.get(rankPart), binRankPart) > 0) {
          selectPool.push(line);
        }
      });
      
      // besoin du timeOut, sinon pas d'application...
      setTimeout(() => {
        this.mainDS.select(selectPool);
        this.mainDS.refresh(); // on rafraichit
        this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
        // ma sélection est OK, je signale pour les éventuels esclaves
        WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
      });
    } else {
      this.state = WIDGET_STATE.SELECTION_COMPLETE; // et on passe à l'état sélectionné
      // pas de sélection, on dispatch tout de suite
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_UPDATED, this)); 
    }
  }

  /* ##############################################################################
                    gestion des filtres (composants drop)
     ############################################################################## */

  /**
   * interception de modification de filtre pour déclencher un refresh
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    super.innerUpdateFilter(e);
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
}

  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFilterDrops(): void {
    this.containerFilter.clear(); // vider le container
    this.filterComponents = []; // on rince la référence aux filtres
  }

  /** construction des dropdowns de filtre selon le contexte */
  protected buildFilterDrops() {
    this.clearFilterDrops(); // on rince tout
    
    // construire unique le rendu, pas besoin d'alimenter les collections tant que la drop n'est pas ouverte
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        const meCompo: ComponentRef<any> = WizComponentManager.instance.addChildTo(
          PivotFilterExtendedRenderer,
          this.containerFilter,
          { instanceProperties: { data: filter } }
        );
        this.filterComponents.push(meCompo);
      });
    }
    this._cdRef.markForCheck();
  }

  /* ##############################################################################
                    gestion des champs à afficher
     ############################################################################## */

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // modification du pool de champs à afficher
    WizEventManager.addListener(this, this.vgDetailZone, ALK_EVENT_TYPE.TIMELINE_SELECT_FIELD, this.refreshFromFields);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this.vgDetailZone, ALK_EVENT_TYPE.TIMELINE_SELECT_FIELD);
  }

  /**
   * afficher ou pas des dates
   * @param e AlkEvent de type TIMELINE_SELECT_FIELD 
   */
  private refreshFromFields(e: AlkEvent): void {
    // console.log('refreshFromFields', e);
    if (!this.templatingMode) {
      // persistence uniquement hors du template, sinon, c'est délégué
      this._saveContext();
    } else {
      this.settings = this.context;
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
  }


  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFieldsChecks(): void {
    this.containerFields.clear(); // vider le container des favoris
    this.fieldsComponents = []; // on rince la référence aux cpx
  }


  
  private buildFieldsChecks(): void {
    this.clearFieldsChecks();
    if (this.context.d) {
      // collecte de la collection d'éléments à restituer
      let idx: number = 0;
      this.context.d.forEach((wcio: WidgetConfigurationItemObj) => {
        if(idx%2 == 0) {

          let fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, wcio.cod); // la définition de colonne
          let filter: IWidgetFilter = {widget: this,wcio: wcio, fkd,  active: true, useSubs: false, useNonUnique: false, onBoolean: true}; 
          const meCompo: ComponentRef<TimelineFieldRenderer> = WizComponentManager.instance.addChildTo(
            TimelineFieldRenderer,
            this.containerFields,
            // { instanceProperties: { data: new PivotField(this, wcio, fkd, null) } }
            { instanceProperties: { data: filter } }
          );
          this.fieldsComponents.push(meCompo);
        }
        idx++;

      });
    }
  }

  /* ##############################################################################
             construction de la frise et des éléments
     ############################################################################## */

    private getModes(): string[] {
      let whos: number = this.context.mem;
      let parts: string[] = [];

      if(WizMathUtils.wizBitwize(whos, TIMELINE_MODE.YEAR) > 0) {
        parts.push('meYear');
      }
      if(WizMathUtils.wizBitwize(whos, TIMELINE_MODE.QUARTER) > 0) {
        parts.push('meTrim');
      }
      if(WizMathUtils.wizBitwize(whos, TIMELINE_MODE.MONTH) > 0) {
        parts.push('meMonth');
      }
      if(WizMathUtils.wizBitwize(whos, TIMELINE_MODE.DAY) > 0) {
        parts.push('meHour');
      }
      return parts;
    }


    private buildToolBar(): ToolbarInput {
      
      let parts: string[] = this.getModes();
      let result: ToolbarInput = {left: 'prev,next,today', center: 'title'};
      if(parts.length > 1) {
        result.right = parts.join(',');
      } else {
        result.right = '';
      }

      return result;
    }


  /** construction graphique */
  private buildCalendar(): void {
    this.calendarOptions.locale = this.model.currentLocaleCode;
    
    this.calendarOptions.headerToolbar = this.buildToolBar();

    this.calendarOptions.initialDate = this.getContextDate();

    this.calendarOptions.initialView = this.getContextMode();
    // this.fullcalendar?.getApi().changeView(this.calendarOptions.initialView);

    // construction des checkBoxes pour les champs en donnée
    this.buildFieldsChecks();

    // reconstruction des zones de filtre
    this.buildFilterDrops();

    // construction du calendar
    this.drawCalendar();
  }

  /**
   * prise du mode d'affichage sur les timelines
   * @override
   * @returns 
   */
  public getContextMode(): string {
    
    let modsArr: string[] = this.getModes();
    
    let result: string = modsArr.length > 0 ? modsArr[0] : 'meYear';
    let mods: Set<string> = new Set(modsArr);
    
    if(!!this.context.cp) {
      this.context.cp.forEach(wkp => {
        if(wkp.cod === 'mode') {
          if(mods.has(wkp.val.toString())) {
            result = wkp.val.toString();
          }
        }
      });
    }
    return result;
  }
  /**
   * reconstruction de la zone du calendar, sur init, changement de FEI, et modif de filtre
   */
  private drawCalendar():void {
    // console.log('drawCalendar');
    
    this.generateResources();
    this.generateEvents();

    // console.log('timeline drawContent', this.events);
    setTimeout(() => {
      this.calendarOptions.events = this.events;
      this.calendarOptions.resources = this.resources;
      this.readyToGo = true;
      this._cdRef.markForCheck();

      if(!!this.fullcalendar) {
        this.fullcalendar?.getApi().refetchEvents();
       
        let eventMap: Map<string, any> = new Map(this.events.map(ev =>[ev.id, ev]));
        let stillAllive: ComponentRef<TimelineEventRenderer>[] = [];
        this.mountedCompos.forEach((compo: ComponentRef<TimelineEventRenderer>) => {
          let data: any = compo.instance.data;
          if(!!data) {
            let newData: any = eventMap.get(data.extendedProps.id);
            if(!!newData) {
              compo.instance.data = newData;
              stillAllive.push(compo)
              // console.log('fullca', compo.instance, newData);
            } else {
              // console.log('ed is dead', compo.instance);
            }
            
          }

        });

        // console.log('fullca', this.fullcalendar, this.compoMap);
        this.mountedCompos = stillAllive; // reset des compos rendus
      }

      

    }, 0); 
  }

  /** construction des entrées de calendrier */
  private generateResources(): void {
    this.resources = [];

    if(this.mainDS.getWorkedSource().length > 1000) {
      let nb: string = this.mainDS.getWorkedSource().length.toString();
      let cleanMess: string = WizTranslationManager.getInstance().translateKey('$.timeline.toomany.$',[nb]);
      // WizPopUp.toast(cleanMess, WizIcon.TOAST_WARNING).show();
      this.tooMany = true;
      this.tooManyLabel = cleanMess;
      return;
    } else {
      this.tooMany = false;
    }

    // console.log('generateResources', this.mainDS.getWorkedSource().length);
    
    // consolidation des ressources par identifiant de clé (pseudo pivot)
    let resourceMap: Map<string, string> = new Map();

    this.mainDS.getWorkedSource().forEach((pl: WidgetLine) => { 
      const keyParts: string[] = [];
      const titleParts: string[] = [];
      if(this.context.c.length > 0) {
        this.context.c.forEach((wcio: WidgetConfigurationItemObj) => {
          let fkd: FeiKeyDef = this.fei.keyMap.get(wcio.cod);
          let part: string;
          if(fkd.isMeasure) {
            part = BaselineEngine.getValue(this.fei, pl, pl.cells.get(wcio.cod));
          } else {
            part = BaselineEngine.getLabel(this.fei, pl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
          }
          if(!!part) {
            titleParts.push(part);
          } 
        });
      }

      this.context.r.forEach((wcio: WidgetConfigurationItemObj) => {    
        let part: string = BaselineEngine.getLabel(this.fei, pl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
        if(!!part) {
          keyParts.push(part);
        } 
      });
      
      let key: string = keyParts.join(WizConstants.KEY_SEPARATOR);
      let title: string = titleParts.join(' - ');
      resourceMap.set(key, title);
    });

    [...resourceMap.keys()].forEach(k => {
      this.resources.push({
        id: k,
        title: resourceMap.get(k),
      });
    });
  }

  /** construction des events calendar depuis mainDS */
  private generateEvents(): void {
    this.events = [];

    if(this.mainDS.getWorkedSource().length > 1000) {
      let nb: string = this.mainDS.getWorkedSource().length.toString();
      let cleanMess: string = WizTranslationManager.getInstance().translateKey('$.timeline.toomany.$',[nb]);
      this.tooMany = true;
      this.tooManyLabel = cleanMess;
      return;
    } else {
      this.tooMany = false;
    }


    const domLen: number = this.scheme.domain.length; // prise du pool de couleurs

    // création des paires de colonnes
    let entries: timelineEntry[] = [];

    for(let i: number = 0; i < this.context.d.length; i++) {
      const start: WidgetConfigurationItemObj = this.context.d[i];
      let end: WidgetConfigurationItemObj;

      if(i< this.context.d.length - 1) { // y'a quelqu'un derrière, c'est bien une paire
        i++;
      }
      end = this.context.d[i];

      const startFkd: FeiKeyDef = BaselineEngine.getKey(this.fei, start.cod); // la définition de colonne
      const endFkd: FeiKeyDef = BaselineEngine.getKey(this.fei, end.cod); // la définition de colonne


      if(!start.ro) { // recyclage du wcio.ro : si true, pas visible
        let contentLabel: string = start.lbl ? start.lbl : this.model.feiEngine.getFeiKeyDefLabel(startFkd); 
        let backgroundColor: string = this.scheme.domain[start.ord % domLen];
        // console.log('gen', backgroundColor, i, contentLabel);
        let textColor: string = '#000000';
    

        if (!!start.csty) {
          let res: styleTrio = this.model.styleEngine.colorSet(start.csty, backgroundColor, textColor, false);
          textColor = res.color;
          backgroundColor = res.backgroundColor;

        }
  
        let entr: timelineEntry = {startField: start.cod, endField: end.cod, label: contentLabel, 
          backgroundColor, textColor, style: start.csty, measureStyle: start.msty,
          };

        entr.startFkd = startFkd;
        entr.endFkd = endFkd;

        if(startFkd?.datakind === FIELD_KIND.WORKFLOW) {
          entr.kindStart = FIELD_KIND.WORKFLOW;
          entr.settingsStart = start.pmdexParams;
        }
        if(endFkd?.datakind === FIELD_KIND.WORKFLOW) {
          entr.kindEnd = FIELD_KIND.WORKFLOW;
          entr.settingsEnd = end.pmdexParams;
        }

        entries.push(entr);
      }
    }

    if(entries.length <= 0) { // vide, pas de datas
      return;
    }

    this.mainDS.getWorkedSource().forEach((wl: WidgetLine) => { 
      entries.forEach((tle: timelineEntry) => {
          

        let start: string = null;
        if(!!tle.startField) {
          if(tle.kindStart === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["startDate"]};
            if(tle.settingsStart) {
              settings = JSON.parse(tle.settingsStart);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.startField)));
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              start = objRes.startDate;
            }
            
          } else {
            start = BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.startField));
          }
        }

        
        let end: string = null;
        if(!!tle.endField) {
          if(tle.kindEnd === FIELD_KIND.WORKFLOW) {
            let settings: any = {"item":"last","fields":["endDate"]};
            if(tle.settingsEnd) {
              settings = JSON.parse(tle.settingsEnd);
            }
            let strRes: string = this.model.workflowEngine.formatWorkflow(settings, BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.endField)));
            if(strRes) {
              let objRes: any = JSON.parse(strRes);
              end = objRes.endDate;
            }
          } else {
            end = BaselineEngine.getValue(this.fei, wl, wl.cells.get(tle.endField));
          }
        }


        let datePart: string[] = [];
        let cleanPart: string[] = [];
        if(start?.length > 8) { // mode time
          datePart.push(WizDateUtils.stringToCalendarTime(start));
          datePart.push(WizDateUtils.stringToCalendarTime(end));
          cleanPart.push(WizDateUtils.timeHourPart(start));
          cleanPart.push(WizDateUtils.timeHourPart(end));
        } else {
          cleanPart.push(WizDateUtils.numberToLocalizedString(Number(start)));
          cleanPart.push(WizDateUtils.numberToLocalizedString(Number(end)));  
          datePart.push(WizDateUtils.dateToString(WizDateUtils.stringToTime(start), 'yyyy-MM-dd'));
          datePart.push(WizDateUtils.dateToString(WizDateUtils.stringToTime(end), 'yyyy-MM-dd'));
        }

        // construction de la clé d'appartenance à la ressource
        const keyParts: string[] = [];
        this.context.r.forEach((wcio: WidgetConfigurationItemObj) => {    
          let part: string = BaselineEngine.getLabel(this.fei, wl, wcio.cod, wcio.fdvf || WizConstants.FIELD_LABEL);
          keyParts.push(part);
        });
        let key: string = keyParts.join(WizConstants.KEY_SEPARATOR);
        // let title: string = titleParts.join(' - ');

        // prise des couleurs, on va éventuellement jouer un style
        let realtextColor: string = tle.textColor;
        let realbackgroundColor: string = tle.backgroundColor;
        // console.log('beforeCss', realtextColor, realbackgroundColor);
        if(tle.measureStyle) {
          try {
            const styleParts: string[] = tle.measureStyle.split(ViewData.KEY_SEPARATOR);
            let fontM;
            let backM;
            let cell: WidgetCell;
            let r;
            if (!!styleParts[0]) {
              fontM = styleParts[0];

              cell = wl.cells?.get(fontM);
              if(cell) {
                r = this.fei.model.cellMatrixEngine.getCellValue(cell.l, cell.k, cell.sc, cell.m);
                if(!!r){
                  realtextColor = r;
                  // console.log('measureStyle front', tle.measureStyle, fontM, backM, wl.cells, r);
                }
              }
            }
            if (!!styleParts[1]) {
              backM = styleParts[1];
              cell = wl.cells?.get(backM);
              if(cell) {
                r = this.fei.model.cellMatrixEngine.getCellValue(cell.l, cell.k, cell.sc, cell.m);
                if(!!r){
                  realbackgroundColor = r;
                  // console.log('measureStyle back', tle.measureStyle, fontM, backM, wl.cells, r);
                }
              }
            }
            // console.log('measureStyle', tle.measureStyle, fontM, backM, wl.cells);
            
          } catch(e: any) {

          }

        }
             
        
        if(tle.style) {
          let parts: string[] = tle.style.split(ViewData.KEY_SEPARATOR); // nommage des style vision_code#code 
          const style: StyleVO = this.model.styleEngine.getStyle(parts[0], parts[1]);
          if (!!style) {
            let neoCss = this.model.widgetEngine.computeStyleForWidgetLine(this.fei, style, wl);
            if(!!neoCss?.color && neoCss?.color !== 'undefined') {
              realtextColor = neoCss.color;
            }
            if(neoCss?.backgroundColor && neoCss?.backgroundColor !== 'undefined') {
              realbackgroundColor = neoCss.backgroundColor;
            }
            // console.log('neoCss', neoCss, realtextColor, realbackgroundColor);
          }
        }

        let realLabel: string = tle.label;
        // on regarde si une cellule correspond au label
        let lblcell = wl.cells?.get(realLabel);

        if(lblcell) {
          let r = this.fei.model.cellMatrixEngine.getCellValue(lblcell.l, lblcell.k, lblcell.sc, lblcell.m);
          if(!!r){
            realLabel = r;
          } else {
            realLabel = '';
          }
        }


        let eventId: string = [wl.uk, tle.startField].join(ViewData.CONTEXT_SEPARATOR);
        let evn = {
            id: eventId, 
            resourceId: key, // wl.uk,
            editable: false, //this.checkMe(fdv),
            title: realLabel,
            start: datePart[0],
            end: datePart[1],
            textColor: realtextColor,
            backgroundColor: realbackgroundColor,
            borderColor: '#00000000',
            extendedProps: {
              context: this.context,
              title: realLabel,
              content: cleanPart.join(' - '),
              color: realtextColor,
              startFkd: tle.startFkd,
              endFkd: tle.endFkd,
              freedataValue: null, 
              id: eventId,
              ds: this.mainDS,
            }
          };
          
          this.events.push(evn);
      });
    });
    // console.log('generateEvents', this.events);
  }


}
